import { SearchStrategy, SearchResult, SearchStrategyProps } from './SearchStrategy';
import { createMilitaryApi } from 'data/military/api';
import { CompletionSuggestBaseResult } from '@zoocasa/go-search';
import { MILITARY_BASES_SEARCH_PREDICTION_GROUP } from 'data/search-predictions';
import SearchPrediction from 'data/search-predictions';
import { dasherize } from '@zoocasa/node-kit/strings/dasherize';
import { parseWktPoint } from 'components/search-es/geo_helper';

/**
 * A search strategy that specifically searches for military bases.
 * This strategy is used either directly for military-only searches
 * or as a component in the InServiceSearchStrategy.
 */
export class MilitaryBaseSearchStrategy implements SearchStrategy {
  /**
   * Executes a search for military bases using the provided query string
   *
   * @param props Search parameters with queryString being the most important
   * @returns Search results containing only military base search predictions
   */
  async search(props: SearchStrategyProps): Promise<SearchResult> {
    const { queryString } = props;

    try {
      // Don't search if query is too short
      if (queryString.length < 2) {
        return {
          searchPredictions: [],
          searchAgentPredictions: [],
        };
      }
      const militaryApi = createMilitaryApi();
      const baseSuggestions = await militaryApi.suggestBase(queryString);

      const searchPredictions = this.transformBaseSuggestionsToSearchPredictions(baseSuggestions);

      return {
        searchPredictions,
        // No agent predictions for military bases
        searchAgentPredictions: [],
      };
    } catch (error) {
      console.error('[MilitaryBaseSearchStrategy] Error:', error);
      return {
        searchPredictions: [],
        searchAgentPredictions: [],
      };
    }
  }

  /**
   * Transforms the military base suggestion results from the API
   * into the format expected by the search system
   *
   * @param baseSuggestions Military base suggestions from the API
   * @returns Formatted search predictions for military bases
   */
  private transformBaseSuggestionsToSearchPredictions(baseSuggestions: CompletionSuggestBaseResult[]): SearchPrediction[] {
    if (!baseSuggestions || baseSuggestions.length === 0) {
      return [];
    }

    // Map each suggestion to the search prediction format
    return baseSuggestions.map((suggestion: CompletionSuggestBaseResult, index) => {
      const geoPoint = suggestion.geoPosition?.pointWkt ? parseWktPoint(suggestion.geoPosition.pointWkt) : { longitude: 0, latitude: 0 };
      const description = `${suggestion.displayName} (${suggestion.militaryBranch})` || '';

      const matchedText = suggestion.matchedText || '';
      const matchedIndex = description.toLowerCase().indexOf(matchedText.toLowerCase());

      // Generate ID in the format <slug>-<military-branch>
      let id;
      // Always dasherize the military branch to ensure URL safety
      const militaryBranchSlug = dasherize(suggestion.militaryBranch.toLowerCase());

      if (suggestion.slug) {
        // If the API provides a slug, ensure it's dasherized and combine with military branch
        const safeSlug = dasherize(suggestion.slug);
        id = `${safeSlug}-${militaryBranchSlug}`;
      } else if (suggestion.id) {
        // If no slug but there is an ID, ensure it's dasherized and append military branch
        const safeId = dasherize(suggestion.id);
        id = `${safeId}-${militaryBranchSlug}`;
      } else {
        // Create a slug from the display name and append the military branch
        const baseNameSlug = dasherize(suggestion.displayName.toLowerCase());
        id = `${baseNameSlug}-${militaryBranchSlug}`;
      }

      const prediction = new SearchPrediction({
        attributes: {
          id,
          description,
          label: 'military',
          group: MILITARY_BASES_SEARCH_PREDICTION_GROUP,
          matchedSubstrings: matchedIndex >= 0 ? [{
            length: matchedText.length,
            offset: matchedIndex,
          }] : [],
          locationId: id,
          data: {
            position: {
              type: 'Point',
              coordinates: [
                geoPoint.longitude,
                geoPoint.latitude,
              ],
            },
          },
          source: 'military',
          identifier: index,
        },
        id,
      });

      return prediction;
    });
  }
}